import type { SubmittedEntry } from '@/api/types/Forms'
import { formatAnswers, formatQuestion } from '@/components/FormsPage/formatting'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Rise } from '@/ui/core/motion'
import { Note, type NoteTone } from '@/ui/core/note'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * Which surface the answers were laid on. A note has to be a step off whatever is under it, and
 * the two places this is shown are opposites: the page is pink, the sheet is white.
 */
export type AnswersSurface = 'ground' | 'sheet'

const GROUND: Record<AnswersSurface, NoteTone> = { ground: 'white', sheet: 'blush' }

/**
 * One answered question, as a note: the question in small caps above the answer, so the answer
 * is the line that reads. One answer stands alone; several are listed with the tick each of them
 * was given, since an answered form is a record of the marks that were made.
 */
const Entry = ({
	entry,
	index,
	ground,
}: { entry: SubmittedEntry; index: number; ground: NoteTone }) => {
	const lines = formatAnswers(entry.answers)

	return (
		<Rise index={index}>
			<Note tone={ground} padded={false} className="p-4">
				<VStack gap={10}>
					<Text size="body3" tone="soft" className="tracking-[0.08em]">
						{formatQuestion(entry.question)}
					</Text>
					{lines.length === 0 ? (
						<Text tone="muted" className="italic">
							No answer
						</Text>
					) : lines.length === 1 ? (
						<Text size="body0" weight="bold">
							{lines[0]}
						</Text>
					) : (
						// Several answers were marked, so each one shows the mark that was made.
						<VStack gap={8}>
							{lines.map((line) => (
								<HStack key={line} gap={10} className="items-start">
									<View className="pt-1">
										<Glyph name="check" size={14} color={colors.leaf} />
									</View>
									<Text size="body0" weight="bold" className="flex-1">
										{line}
									</Text>
								</HStack>
							))}
						</VStack>
					)}
				</VStack>
			</Note>
		</Rise>
	)
}

/**
 * What was sent, question by question. Shared by the form's own page and by the sheet on Care
 * that steps into it, so a record reads the same however it was opened. Only the notes' own
 * colour changes, because white notes vanish inside the white sheet.
 */
export const SubmittedAnswers = ({
	entries,
	on = 'ground',
}: {
	entries: SubmittedEntry[]
	on?: AnswersSurface
}) => {
	const ground = GROUND[on]

	if (entries.length === 0) {
		return (
			<Note tone={ground} padded={false} className="p-4">
				<Text tone="soft">This form was submitted without any answers.</Text>
			</Note>
		)
	}

	return (
		<VStack gap={12}>
			<Text size="body3" tone="soft" className="tracking-[0.08em]">
				Your answers
			</Text>
			{entries.map((entry, index) => (
				<Entry
					// Questions repeat within a form, and the id is not always there.
					key={`${entry.questionId ?? 'entry'}-${index}`}
					entry={entry}
					index={index}
					ground={ground}
				/>
			))}
		</VStack>
	)
}
